import {
  Box,
  Button,
  CSSObject,
  SxProps,
  Theme,
  Typography,
  TypographyProps,
  styled,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import RouterLink from "../RouterLink";
import { IDeviceTableRow } from "../../../pages/devices/hooks/useDeviceTableRows";
import { useMemo } from "react";

export const StyledMenuBox = styled(Box)(
  ({ theme, sx }): CSSObject => ({
    "&": {
      cursor: "pointer",
      padding: "2px 32px 2px 10px",
      borderRadius: "8px",
      display: "flex",
      justifyContent: "center",
      height: "28px",
      color: theme.palette.text.primary,
      backgroundColor: "transparent",
      border: `1px solid ${theme.palette.otherOutlineBorder.main}`,
      "&:hover": {
        backgroundColor: theme.palette.primary.shades?.["8p"],
        borderColor: theme.palette.primary.shades?.["30p"],
        color: theme.palette.primary.dark,
      },
      "&.inActive": {
        backgroundColor: "transparent",
        color: theme.palette.otherStandardInputLine.main,
      },
      ...sx,
    },
  })
);

const menuBoxChipStyles: SxProps<Theme> = {
  display: "flex",
  width: "fit-content",
  padding: "0.25em 0.5em",
  alignItems: "center",
  cursor: "auto",
  borderRadius: "4px",
  "&:hover": {
    backgroundColor: "none",
  },
};

type ChipComponentProps = {
  activeState?: boolean;
  iconComponent?: React.ReactNode;
  menuBoxStyles?: SxProps<Theme>;
  label: string;
  variant?: "routerLink" | "button" | "chip";
  routeLink?: string;
  routeData?: IDeviceTableRow;
  textStyles?: SxProps<Theme>;
  textVariant?: TypographyProps["variant"];
  onClick?: () => void;
};

const ChipComponent: React.FC<ChipComponentProps> = ({
  activeState = true,
  iconComponent,
  label,
  routeData = {},
  menuBoxStyles,
  routeLink = "",
  onClick,
  textStyles,
  textVariant = "buttonMedium",
  variant = "routerLink",
}) => {
  const Component: React.ElementType =
    variant === "routerLink" ? RouterLink : variant === "button" ? Button : Box;

  const styledMenuBoxSx = useMemo(() => {
    return {
      ...(variant === "chip" ? menuBoxChipStyles : {}),
      ...menuBoxStyles,
    };
  }, [menuBoxStyles, variant]);

  return (
    <Component
      onClick={onClick}
      {...(variant === "routerLink"
        ? { to: routeLink, state: { deviceDatum: routeData } }
        : variant === "button"
        ? { onClick }
        : {})}
    >
      <StyledMenuBox
        className={activeState ? "" : "inActive"}
        sx={styledMenuBoxSx}
      >
        <Stack direction="row" alignItems="center">
          {iconComponent && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginRight: "0.25em",
              }}
            >
              {iconComponent}
            </Box>
          )}

          <Typography
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              ...textStyles,
            }}
            variant={textVariant}
          >
            {label}
          </Typography>
        </Stack>
      </StyledMenuBox>
    </Component>
  );
};

export default ChipComponent;
